import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';

// Cache permissions to avoid repeated API calls
let permissionsCache = null;
let cacheRole = null;

export function usePermissions() {
  const { role, token } = useSelector(s => s.auth);
  const [permissions, setPermissions] = useState(permissionsCache || {});
  const [loading, setLoading] = useState(!permissionsCache);

  // Admin/Developer always have full access — no need to check
  const isFullAccess = ['admin', 'developer'].includes(role);

  useEffect(() => {
    if (!token) return;
    if (isFullAccess) { setPermissions({}); setLoading(false); return; }
    if (permissionsCache && cacheRole === role) {
      setPermissions(permissionsCache); setLoading(false); return;
    }
    api.get('/rbac/my-permissions')
      .then(r => {
        permissionsCache = r.permissions || {};
        cacheRole = role;
        setPermissions(permissionsCache);
      })
      .catch(() => setPermissions({}))
      .finally(() => setLoading(false));
  }, [role, token]);

  // Check if user can do an action on a menu
  const can = (menuCode, action = 'can_view') => {
    if (isFullAccess) return true;
    return !!permissions[menuCode]?.[action];
  };

  // Shorthand helpers
  const canView     = (mc) => can(mc, 'can_view');
  const canAdd      = (mc) => can(mc, 'can_add');
  const canEdit     = (mc) => can(mc, 'can_edit');
  const canDelete   = (mc) => can(mc, 'can_delete');
  const canApprove  = (mc) => can(mc, 'can_approve');
  const canReject   = (mc) => can(mc, 'can_reject');
  const canBlock    = (mc) => can(mc, 'can_block');
  const canDownload = (mc) => can(mc, 'can_download');

  // Clear cache on logout
  const clearCache = () => { permissionsCache = null; cacheRole = null; };

  return { permissions, loading, can, canView, canAdd, canEdit, canDelete, canApprove, canReject, canBlock, canDownload, clearCache, isFullAccess };
}

// Usage example:
// const { canView, canApprove } = usePermissions();
// if (!canView('applications')) return <AccessDenied />
// {canApprove('applications') && <button>Approve</button>}